/**
 * Factory function for creating entity-specific draft hooks
 *
 * This eliminates duplicate code by generating typed draft hooks for any entity type.
 *
 * @example
 * // In feature/employees/hooks/useEmployeeDraft.ts
 * import { ENTITY_TYPES } from "@/shared/contexts/DraftContext/DraftContext";
 * import { createEntityDraftHook } from "@/shared/hooks/drafts";
 * import type { EmployeeInput } from "../schemas/employee.schema";
 *
 * export const useEmployeeDraft = createEntityDraftHook<EmployeeInput>(
 *   ENTITY_TYPES.EMPLOYEE,
 *   "useEmployeeDraft"
 * );
 */
import { useContext, useCallback, useMemo } from "react";
import {
  DraftContext,
  type Draft,
  type EntityType,
} from "@/shared/contexts/DraftContext/DraftContext";

/**
 * Return type for entity draft hooks
 */
export type EntityDraftHook<T> = {
  /** Initialize a draft with original values for comparison */
  initializeDraft: (entityId: string | null, originalData: Partial<T>) => void
  /** Save a complete draft for an entity */
  saveDraft: (
    entityId: string | null,
    formData: Partial<T>,
    changedFields: string[],
  ) => void
  /** Update a single field in a draft */
  updateDraftField: (
    entityId: string | null,
    fieldName: string,
    fieldValue: unknown,
  ) => void
  /** Remove a draft for an entity */
  removeDraft: (entityId: string | null) => void
  /** Get a draft for an entity */
  getDraft: (entityId: string | null) => Draft<T> | null
  /** Check if a draft exists for an entity */
  hasDraft: (entityId: string | null) => boolean
  /** Get list of changed fields for an entity */
  getChangedFields: (entityId: string | null) => string[]
  /** Clear all drafts for all entities */
  clearAllDrafts: () => void
};

/**
 * Options for createEntityDraftHook
 */
export type CreateEntityDraftHookOptions = {
  /** Whether to use useCallback for memoization (default: false for simplicity) */
  memoize?: boolean
};

/**
 * Creates a typed draft hook for a specific entity type
 *
 * @param entityType - The entity type constant from ENTITY_TYPES
 * @param hookName - Name of the hook for error messages
 * @param options - Optional configuration
 * @returns A hook function that provides draft operations for the entity type
 */
export function createEntityDraftHook<T>(
  entityType: EntityType,
  hookName: string,
  options: CreateEntityDraftHookOptions = {},
): () => EntityDraftHook<T> {
  const { memoize = false } = options;

  if (memoize) {
    // Return memoized hook version
    return function useEntityDraftMemoized(): EntityDraftHook<T> {
      const context = useContext(DraftContext);

      if (!context) {
        throw new Error(
          `${hookName} must be used within a DraftContextProvider`,
        );
      }

      const initializeDraft = useCallback(
        (entityId: string | null, originalData: Partial<T>) =>
          context.initializeDraft(entityType, entityId, originalData),
        [context],
      );

      const saveDraft = useCallback(
        (entityId: string | null, formData: Partial<T>, changedFields: string[]) =>
          context.saveDraft(entityType, entityId, formData, changedFields),
        [context],
      );

      const updateDraftField = useCallback(
        (entityId: string | null, fieldName: string, fieldValue: unknown) =>
          context.updateDraftField(entityType, entityId, fieldName, fieldValue),
        [context],
      );

      const removeDraft = useCallback(
        (entityId: string | null) => context.removeDraft(entityType, entityId),
        [context],
      );

      const getDraft = useCallback(
        (entityId: string | null) => context.getDraft<T>(entityType, entityId),
        [context],
      );

      const hasDraft = useCallback(
        (entityId: string | null) => context.hasDraft(entityType, entityId),
        [context],
      );

      const getChangedFields = useCallback(
        (entityId: string | null) => context.getChangedFields(entityType, entityId),
        [context],
      );

      const clearAllDrafts = useCallback(
        () => context.clearAllDrafts(),
        [context],
      );

      return useMemo(() => ({
        initializeDraft,
        saveDraft,
        updateDraftField,
        removeDraft,
        getDraft,
        hasDraft,
        getChangedFields,
        clearAllDrafts,
      }), [
        initializeDraft,
        saveDraft,
        updateDraftField,
        removeDraft,
        getDraft,
        hasDraft,
        getChangedFields,
        clearAllDrafts,
      ]);
    };
  }

  // Return non-memoized hook version (default)
  return function useEntityDraft(): EntityDraftHook<T> {
    const context = useContext(DraftContext);

    if (!context) {
      throw new Error(
        `${hookName} must be used within a DraftContextProvider`,
      );
    }

    return {
      initializeDraft: (entityId: string | null, originalData: Partial<T>) =>
        context.initializeDraft(entityType, entityId, originalData),
      saveDraft: (entityId: string | null, formData: Partial<T>, changedFields: string[]) =>
        context.saveDraft(entityType, entityId, formData, changedFields),
      updateDraftField: (entityId: string | null, fieldName: string, fieldValue: unknown) =>
        context.updateDraftField(entityType, entityId, fieldName, fieldValue),
      removeDraft: (entityId: string | null) =>
        context.removeDraft(entityType, entityId),
      getDraft: (entityId: string | null) =>
        context.getDraft<T>(entityType, entityId),
      hasDraft: (entityId: string | null) =>
        context.hasDraft(entityType, entityId),
      getChangedFields: (entityId: string | null) =>
        context.getChangedFields(entityType, entityId),
      clearAllDrafts: context.clearAllDrafts,
    };
  };
}
